import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Field, useForm, useFormState } from 'react-final-form';

import ActionsSection from '../actions-sections/ActionsSection';
import { getAccessUsers, loadMoreAccessUsers } from '../../../redux/projects/projectsActions';
import AccessContainers from '../../../components/access-containers/AccessContainers';
import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import SearchInput from '../../../components/common-inputs/SearchInput';
import arraysHaveSameElements from '../../../utils/arraysHaveSameElements';

import scss from './AccessUsersSection.module.scss';

const AccessUsersForm = ({ onSubmit, initialValues }) => {
  const { t } = useTranslation('projects');
  const dispatch = useDispatch();
  const { restart, change } = useForm();
  const { values, valid } = useFormState();

  const [searchStr, setSearchStr] = useState('');

  const editProject = useSelector((state) => state.projects.editProject);
  const isUsersSectionLoading = useSelector((state) => state.projects.isUsersSectionLoading);
  const isAccessUsersLoading = useSelector((state) => state.projects.isAccessUsersLoading);
  const { accessUsers, totalAccessUsers } = useSelector((state) => state.projects);

  const inactiveUsers = useMemo(
    () => accessUsers.filter((user) => !values.users.some((activeUser) => activeUser.id === user.id)),
    [values, accessUsers],
  );

  const isDirty = useMemo(
    () =>
      !arraysHaveSameElements(
        values.users.map(({ id }) => id),
        initialValues.users.map(({ id }) => id),
      ),
    [values, initialValues],
  );

  const handleDiscardsChanges = useCallback(() => {
    restart();
  }, [restart]);

  const handleActiveUser = useCallback(
    (user) => {
      const sortedIds = [...values.users, user].sort();
      change('users', sortedIds);
    },
    [values, change],
  );

  const handleInactiveUser = useCallback(
    ({ id }) => {
      const sortedIds = [...values.users.filter((containedUser) => containedUser.id !== id)].sort();
      change('users', sortedIds);
    },
    [values, change],
  );

  const handleSearchUsers = useCallback(
    (newSearch) => {
      setSearchStr(newSearch);
      dispatch(getAccessUsers({ search: newSearch, page: 1 }));
    },
    [dispatch],
  );

  const handleLoadMoreUsers = useCallback(
    (newPage) => {
      dispatch(loadMoreAccessUsers({ search: searchStr, page: newPage }));
    },
    [dispatch, searchStr],
  );

  useEffect(() => {
    dispatch(getAccessUsers({ search: '', page: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={scss.form}>
      <WithLabel label={t('assignUsers')} variant={LABEL_STYLES.ROW} extraClassName={scss.searchLabel} isBold>
        <SearchInput
          value={searchStr}
          onChange={handleSearchUsers}
          size='sm'
          disabled={!editProject || isUsersSectionLoading}
          maxWidth={240}
        />
      </WithLabel>

      <Field name='users'>
        {({ input }) => (
          <AccessContainers
            onInactive={handleInactiveUser}
            onActive={handleActiveUser}
            inactive={inactiveUsers}
            active={input.value}
            disabled={!editProject || isUsersSectionLoading}
            isLoading={isAccessUsersLoading}
            searchStr={searchStr}
            onLoadMore={handleLoadMoreUsers}
            totalElements={totalAccessUsers}
            inactiveLoadedSize={accessUsers.length}
            classes={{ wrapper: scss.containerWrapper }}
          />
        )}
      </Field>

      <ActionsSection
        dirty={isDirty}
        valid={valid}
        onDiscard={handleDiscardsChanges}
        onSubmit={onSubmit}
        isLoading={isUsersSectionLoading}
      />
    </div>
  );
};

export default memo(AccessUsersForm);
